import type * as api from '@gitbook/api-client';
import type { IntegrationCategory } from '@gitbook/api-client';

import type {
    NavigationChangeRequestAgentSideSheet,
    NavigationChangeRequestSideSheet,
    NavigationCollectionSideSheet,
    NavigationCommentsSideSheet,
    NavigationIntegrationsModal,
    NavigationSiteSideSheet,
    NavigationSpaceSideSheet,
} from './side-sheets';

export interface LocationDescriptor {
    pathname: string;
    search?: string;
    hash?: string;
}

export type NavigationScreenState =
    | NavigationScreenRoot
    | NavigationScreenJoin
    | NavigationScreenErrorNotFound
    | NavigationScreenIntegrationsState
    | NavigationScreenIntegrationState
    | NavigationScreenHomeState
    | NavigationScreenUserState
    | NavigationScreenAccountSettings
    | NavigationScreenOrgInvite
    | NavigationScreenOrgWithBaseName
    | NavigationScreenOrgJoin
    | NavigationScreenOrgSettings
    | NavigationScreenWelcome
    | NavigationScreenSpaceState
    | NavigationScreenCollectionState
    | NavigationScreenContentKitBuilder
    | NavigationScreenTrashState
    | NavigationScreenSiteState
    | NavigationScreenSitesListState
    | NavigationScreenAgentsState
    | NavigationScreenChangeRequestsState
    | NavigationScreenOpenAPISpecsHomeState
    | NavigationScreenOpenAPISpecState
    | NavigationScreenTranslationState;

export interface BaseNavigationScreen {
    /** Hash in the url, without the leading # */
    hash?: string;

    /** Search query in the palette / public search */
    query?: string;

    askAI?: boolean;

    /** Login infos passed by the SSO backend  */
    ssoLogin?: {
        noSSO?: 'account_exists';
        email?: string;
    };
}

/**
 * Screen when url cannot be resolved.
 */
export interface NavigationScreenErrorNotFound extends BaseNavigationScreen {
    type: 'error_notfound';
}

/**
 * Screen for showing the sign up page, which is very similar to the sign in page, but
 * has a style more geared towards encouraging users to join GitBook.
 */
export interface NavigationScreenJoin extends BaseNavigationScreen {
    type: 'join';
}

/**
 * Main screen when opening the app or an organization.
 * It redirects to the latest visited content.
 */
export interface NavigationScreenRoot extends BaseNavigationScreen {
    type: 'root';
    organization?: string;
}

/**
 * Main screen displaying the content for the org or user.
 */
export interface NavigationScreenHomeState extends BaseNavigationScreen {
    type: 'home';
    organization?: string;
}

/**
 * Screen listing integrations
 */
export interface NavigationScreenIntegrationsState extends BaseNavigationScreen {
    type: 'integrations';
    organization?: string;
    /** Search query to filter the integrations list */
    search?: string;
    /** Category param to filter the integrations list */
    category?: IntegrationCategory;
    /** Whether to fetch integrations from marketplace or owned by the current org */
    provider?: IntegrationsProvider;
}

export enum IntegrationsProvider {
    Marketplace = 'marketplace',
    Organization = 'organization',
}

/**
 * Screen for an integration
 */
export interface NavigationScreenIntegrationState extends BaseNavigationScreen {
    type: 'integration';
    organization?: string;
    integration: string;
    tab?: 'about' | 'installations';
    installation?: {
        step: 'flow' | 'configure';
        context?: {
            type: 'site' | 'space';
            id: string;
        };
    };
}

/**
 * Info screen on a user
 */
export interface NavigationScreenUserState extends BaseNavigationScreen {
    type: 'user';
    user: string;
}

/**
 * State of the navigation for onboarding and when creating an org
 */
export interface NavigationScreenWelcome extends BaseNavigationScreen {
    type: 'welcome';
    /** Type of organization being created */
    organizationType?: 'business' | 'community';
}

/**
 * Base state for all space screens.
 */
type NavigationScreenSpaceBaseState = {
    type: 'space';

    /** ID of the space being visited  */
    space: string;

    /** ID of the organization owning the space */
    organization?: string;

    /** Path of the current page */
    page: string;

    /** Display mode for the content */
    mode?: 'preview' | 'diff';

    /** ID of a revision to open. Can be undefined, and the latest revision will be loaded */
    revision?: string;

    /** ID of a reusable content to display. */
    reusableContent?: string;
} & BaseNavigationScreen;

/**
 * State of the navigation when viewing the integrations modal in a space
 */
export type NavigationScreenSpaceIntegrationsModalState = Omit<
    NavigationScreenSpaceState,
    'sideSheet'
> & {
    sideSheet: NavigationIntegrationsModal;
};

/**
 * State of the navigation when viewing a revision in a space (including the primary content).
 */
export type NavigationScreenSpaceRevisionState = NavigationScreenSpaceBaseState & {
    /** ID or number of a change request to open in the space. */
    changeRequest?: undefined;

    /** Side sheets when viewing a revision in a space */
    sideSheet?: NavigationSpaceSideSheet;
};

/**
 * State of the navigation when viewing a change request in a space.
 */
export type NavigationScreenSpaceChangeRequestState = NavigationScreenSpaceBaseState & {
    /** ID or number of a change request to open in the space. */
    changeRequest: string | number;

    /** Sidesheets for change requests */
    sideSheet?: NavigationChangeRequestSideSheet;
};

export function isNavigationScreenSpaceChangeRequestState(
    screen: NavigationScreenState
): screen is NavigationScreenSpaceChangeRequestState {
    return (
        screen.type === 'space' && 'changeRequest' in screen && screen.changeRequest !== undefined
    );
}

/**
 * State of the navigation when viewing a space.
 */
export type NavigationScreenSpaceState =
    | NavigationScreenSpaceRevisionState
    | NavigationScreenSpaceChangeRequestState;

/**
 * State of the navigation when viewing a collection.
 */
export type NavigationScreenCollectionState = {
    type: 'collection';

    /** ID of the collection */
    collection: string;

    /** ID of the organization owning the collection */
    organization?: string;

    /** Sidesheets for collections */
    sideSheet?: NavigationCollectionSideSheet;
} & BaseNavigationScreen;

/**
 * State of the navigation when viewing a site.
 *
 * Tabs: spaces, insights, settings.
 */

export enum BillingQueryParams {
    UpgradeSuccess = 'upgrade_success',
}

export interface NavigationScreenSiteState extends BaseNavigationScreen {
    type: 'site';

    /** ID of the site */
    site: string;

    /** ID of the organization owning the site */
    organization?: string;

    tab?: NavigationScreenSiteTab;

    /** Sidesheets for sites */
    sideSheet?: NavigationSiteSideSheet;

    /** Billing URL params used for pricing flows */
    billing?: BillingQueryParams;
}

/**
 * State of the navigation when viewing the integrations modal in a site
 */
export type NavigationScreenSiteIntegrationsModalState = Omit<
    NavigationScreenSiteState,
    'sideSheet'
> & {
    sideSheet: NavigationIntegrationsModal;
};

export type NavigationScreenSiteTab =
    | NavigationScreenSiteTabInsights
    | NavigationScreenSiteTabSettings
    | NavigationScreenSiteTabPreview
    | NavigationScreenSiteTabCustomization;

export const NavigationSiteInsightsReports = [
    'traffic',
    'search',
    'ask',
    'links',
    'openapi',
    'pages',
    'ads',
    'urls',
] as const;

export type NavigationScreenSiteTabInsights = {
    type: 'insights';
    report: (typeof NavigationSiteInsightsReports)[number];
    filters?: Partial<Pick<api.SiteInsightsQueryEventsAggregation, 'range' | 'where' | 'groupBy'>>;
};

export const NavigationSiteSettingsSections = [
    'general',
    'audience',
    'schema',
    'domains',
    'redirects',
    'ai',
    'structure',
    'sponsorship',
    'legal',
    'plan',
] as const;

export type NavigationSiteSettingsSection = (typeof NavigationSiteSettingsSections)[number];

export const NavigationSiteCustomizationTabs = ['general', 'layout', 'configure'] as const;

export type NavigationScreenSiteTabSettings = {
    type: 'settings';
    section?: NavigationSiteSettingsSection;
};

export type NavigationScreenSiteTabPreview = {
    type: 'preview';
};

export type NavigationScreenSiteTabCustomization = {
    type: 'customization';
    siteSpaceId?: string;
    tab: (typeof NavigationSiteCustomizationTabs)[number];
};

/**
 * State of the navigation when viewing the screen listing sites.
 */
export interface NavigationScreenSitesListState extends BaseNavigationScreen {
    type: 'sites';

    /** ID of the organization owning the site */
    organization?: string;

    /** Published status for sites used to filter the sites list.
     * TO-DO: use API here when ready (ie: api.SitePublishedStatus).
     */
    status?: 'published' | 'unpublished';

    /** Search query to filter the sites list */
    search?: string;
}

/**
 * State of the navigation when sharing invites.
 */
export interface NavigationScreenOrgInvite extends BaseNavigationScreen {
    type: 'org_invite';
    organization: string;
}

/**
 * State of the navigation when opening a login link with the org basename in it.
 * These urls are mostly legacy.
 */
export interface NavigationScreenOrgWithBaseName extends BaseNavigationScreen {
    type: 'org_with_basename';
    organizationBaseName: string;
}

/**
 * State of the navigation when viewing an invite.
 */
export interface NavigationScreenOrgJoin extends BaseNavigationScreen {
    type: 'org_join';
    organization: string;
    invite: string;
}

export interface NavigationOrgSettingsPlansTab {
    type: 'plans';
    interval?: 'monthly' | 'yearly';
    switchToProduct?: string;
}

export type NavigationOrgSettingsTab =
    | NavigationOrgSettingsTabWithoutBilling
    | NavigationOrgSettingsBillingTab
    | NavigationOrgSettingsMigrateTab;

export type NavigationOrgSettingsTabWithoutBilling =
    | { type: 'profile' }
    | {
          type: 'members';
          memberId?: string;
          detailsTab?: string;
      }
    | {
          type: 'teams';
          teamId?: string;
          detailsTab?: string;
      }
    | NavigationOrgSettingsPlansTab
    | { type: 'sso' }
    | { type: 'invites' }
    | { type: 'rules' }
    | { type: 'agents' };

export interface NavigationOrgSettingsBillingTab {
    type: 'billing';
    returnScreen?: NavigationScreenState;
    publishSiteId?: string;
    changeSiteType?: {
        siteId: string;
        siteType: string;
    };
}

export interface NavigationOrgSettingsMigrateTab {
    type: 'upgrade';
}

/**
 * State of the navigation when viewing organization settings.
 */
export interface NavigationScreenOrgSettingsWithoutBilling extends BaseNavigationScreen {
    type: 'org_settings';
    organization: string;
    tab: NavigationOrgSettingsTabWithoutBilling;
}

/**
 * State of the navigation when viewing the organization migrate screen
 */
export interface NavigationScreenOrgMigrateSettings extends BaseNavigationScreen {
    type: 'org_settings';
    organization: string;
    tab: NavigationOrgSettingsMigrateTab;
}

/**
 * State of the navigation when viewing just the organization billing settings
 */
export interface NavigationScreenOrgBillingSettings extends BaseNavigationScreen {
    type: 'org_settings';
    organization: string;
    tab: NavigationOrgSettingsBillingTab;
}

export type NavigationScreenOrgSettings =
    | NavigationScreenOrgSettingsWithoutBilling
    | NavigationScreenOrgBillingSettings
    | NavigationScreenOrgMigrateSettings;

export type NavigationAccountSettingsTab =
    | 'profile'
    | 'developer'
    | 'organizations'
    | 'notification';

/**
 * State of the navigation when viewing account settings.
 */
export interface NavigationScreenAccountSettings extends BaseNavigationScreen {
    type: 'account';
    tab: NavigationAccountSettingsTab;
}

/**
 * State for the ContentKit builder
 */
export interface NavigationScreenContentKitBuilder extends BaseNavigationScreen {
    type: 'contentkit_builder';
    content: string;
}

/**
 * State for viewing and managing the trash content.
 */
export interface NavigationScreenTrashState extends BaseNavigationScreen {
    type: 'trash';
    organization: string;
}

/**
 * State for home of OpenAPI specs in an organization.
 */
export interface NavigationScreenOpenAPISpecsHomeState extends BaseNavigationScreen {
    type: 'openapi_specs';
    organization?: string;
}

/**
 * State for displaying an OpenAPI spec.
 */
export interface NavigationScreenOpenAPISpecState extends BaseNavigationScreen {
    type: 'openapi_spec';
    organization?: string;
    /**
     * Slug of the OpenAPI spec to display.
     */
    spec: string;
}

/**
 * Filters that can be applied to the list of change requests.
 */
export type NavigationScreenChangeRequestsFilters = {
    status?: api.ChangeRequestStatus;
    contributor?: string;
    requestedReview?: string;
    creator?: string;
    space?: string;
    conversationsIssuesCluster?: string;
};

/**
 * State for the organization change requests screen.
 */
export interface NavigationScreenChangeRequestsState extends BaseNavigationScreen {
    type: 'change_requests';
    organization: string;
    /** Filters applied to the list of change requests */
    filters?: NavigationScreenChangeRequestsFilters;
    /** Identifier of the currently selected change request */
    selected?: {
        spaceId: string;
        changeRequestNumber: number;
        sideSheet?: NavigationCommentsSideSheet | NavigationChangeRequestAgentSideSheet;

        /**
         * ID of the page being highlighted / commented on.
         */
        pageId?: string;
    };
}

/**
 * State for the agent screen.
 */
export type NavigationScreenAgentsViewTasks = {
    type: 'tasks';
    filters?: {
        status: 'all' | 'triage' | 'done' | 'archived';
        space?: string;
        cluster?: string;
    };
    agentTask?: {
        id: string;
        tab: 'instructions' | 'chat' | 'context' | 'changes';
    };
};
export interface NavigationScreenAgentsState extends BaseNavigationScreen {
    type: 'agents';
    organization: string;
    view: NavigationScreenAgentsViewTasks;
    dialog?: {
        type: 'settings';
        tab: 'connectors' | 'sites' | 'advanced';
    };
}

/**
 * State for the translation screen.
 */
export interface NavigationScreenTranslationState extends BaseNavigationScreen {
    type: 'translations';
    organization?: string;
    view: 'overview' | 'glossary';
}
